import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";

interface Props {
  level: "campaign" | "adset";
  accountId: string;
  targetId: string;
  campaignIdForAdsetInvalidate?: string; // only when level === "adset"
  currentDailyCents: number | null | undefined;
  currentLifetimeCents: number | null | undefined;
  currency?: string;
  onClose: () => void;
}

type BudgetKind = "daily" | "lifetime";

export default function BudgetEditDialog({
  level, accountId, targetId, campaignIdForAdsetInvalidate,
  currentDailyCents, currentLifetimeCents, currency, onClose,
}: Props) {
  const qc = useQueryClient();
  const initialKind: BudgetKind = currentLifetimeCents != null && currentDailyCents == null
    ? "lifetime"
    : "daily";
  const initialMajor =
    initialKind === "lifetime"
      ? (currentLifetimeCents ?? 0) / 100
      : (currentDailyCents ?? 0) / 100;

  const [kind, setKind] = useState<BudgetKind>(initialKind);
  const [major, setMajor] = useState<string>(initialMajor > 0 ? initialMajor.toString() : "");

  const path = level === "campaign"
    ? "/api/v1/ad-accounts/{accountId}/campaigns/{campaignId}/budget"
    : "/api/v1/ad-accounts/{accountId}/adsets/{adsetId}/budget";

  const save = useMutation({
    mutationFn: async () => {
      const cents = Math.round(Number(major) * 100);
      if (!cents || cents <= 0) throw new Error("金额必须 > 0");
      const body = kind === "daily"
        ? { dailyCents: cents }
        : { lifetimeCents: cents };
      const params = level === "campaign"
        ? { path: { accountId, campaignId: targetId } }
        : { path: { accountId, adsetId: targetId } };
      const { error, response } = await api.PATCH(path as never, { params, body } as never);
      if (error || !response.ok) throw error || new Error("保存失败");
    },
    onSuccess: () => {
      if (level === "campaign") {
        qc.invalidateQueries({ queryKey: ["campaigns:base", accountId] });
        qc.invalidateQueries({ queryKey: ["campaigns:insights", accountId] });
        qc.invalidateQueries({ queryKey: ["campaigns", accountId] });
      } else if (campaignIdForAdsetInvalidate) {
        qc.invalidateQueries({ queryKey: ["adsets", accountId, campaignIdForAdsetInvalidate] });
      } else {
        qc.invalidateQueries({ queryKey: ["adsets", accountId] });
      }
      toast.success("预算已更新");
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">
            修改{level === "campaign" ? "广告系列" : "广告组"}预算
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          className="p-5 space-y-4 text-sm"
        >
          <div className="text-xs text-muted-foreground">
            Meta 端日预算与总预算同一时刻只能存在一个;切换会自动覆盖原值。
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={kind === "daily"}
                onChange={() => setKind("daily")}
              />
              日预算
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={kind === "lifetime"}
                onChange={() => setKind("lifetime")}
              />
              总预算
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium">金额{currency ? `(${currency})` : ""}</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={major}
              onChange={(e) => setMajor(e.target.value)}
              required
              placeholder="例:50.00"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {save.isPending ? "保存中…" : "保存"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
